import { useState } from "react";
import styled from "@emotion/styled";
import { Alert, Chip, Fade, Grid, Tooltip } from "@mui/material";
import { Modal } from "@/components/_common";
import PolarisIcon from '@/components/_common/PolarisIcon';
import { useAppSelector } from "@/redux/hooks";
import { selectFileTypes } from "@/redux/selectors/constantsSelectors";
import { FileFormat } from "@/constants/constants";
import { insertWordBreaks } from "@/_utils/utils";
import VideoPlayer from '@/components/_common/VideoPlayer/VideoPlayer';
import { selectUserDarkMode } from "@/redux/selectors/authSelectors";
import PdfPReview from "@/components/_common/PdfPreview";
import StringPreview, { STRING_PREVIEW_TYPES } from "@/components/_common/StringPreview";
import MarkdownPreview from "@/components/_common/MarkdownPreview";

type FilePreviewProps = {
  file?: {
    name: string;
    label?: string;
    url?: string;
    type?: FileFormat;
  };
  onClose: () => void;
};

const JobRelatedFilePreview = ({ file, onClose }: FilePreviewProps) => {
  const fileTypes = useAppSelector(selectFileTypes);
  const isDarkMode = useAppSelector(selectUserDarkMode);
  const [error, setError] = useState<string | null | boolean>(null);

  const handleError = () => {
    setError('There was an error retrieving the file.');
  };

  if (!file?.type || !file.url) {
    return null;
  }

  const fileType = fileTypes[file.type];

  const renderComponentByType = () => {
    switch (fileType) {
      case 'video':
        return <VideoPlayer url={file.url} poster="" />;
      case 'audio':
        return (
          <Fade in timeout={600}>
            <AudioPreview isdark={isDarkMode ? 1 : 0}>
              <audio controls src={file.url} onError={handleError}>
                Your browser does not support the
                <code>audio</code> element.
              </audio>
            </AudioPreview>
          </Fade>
        );
      case 'image':
        return <img src={file.url || undefined} alt="preview image" width="100%" />;
      case 'subtitle':
      case 'other':
        if (file.type === 'md' && !!file.url) {
          return <MarkdownPreview url={file.url} />
        } else if (file.type === 'pdf' && !!file.url) {
          return <PdfPReview source={file.url} />
        } else if (STRING_PREVIEW_TYPES.includes(file.type as string) && !!file.url) {
          return <StringPreview language={file.type} url={file.url} />
        } else {
          return <CannotBePreviewed>This file cannot be previewed</CannotBePreviewed>;
        }
      default:
        break;
    }
  };

  return (
    <Modal
      open={!!file}
      onClose={onClose}
      maxWidth={1300}
      icon={<PolarisIcon icon="preview" />}
      title={
        <>
          <strong>Preview:</strong> {insertWordBreaks(file.name)}
        </>
      }
    >
      {error && (
        <Grid container justifyContent="center" sx={{ mt: 8 }}>
          <Alert severity="error" sx={{ display: 'inline-flex' }}>
            {error}
          </Alert>
        </Grid>
      )}

      {!error && (
        <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: 100 }}>
          <Grid container flexDirection="column">
            <Metadata>
              <Tooltip
                title={file.label}
                key={file.label}
                componentsProps={{
                  tooltip: {
                    sx: {
                      textTransform: 'capitalize',
                      hyphens: 'manual',
                      wordBreak: 'break-all',
                      lineHeight: '1.2em'
                    }
                  }
                }}
              >
                <CapitalizedChip label={file.label} size="small" variant="filled" color="info" />
              </Tooltip>
            </Metadata>
            {renderComponentByType()}
          </Grid>
        </Grid>
      )}
    </Modal>
  )
};

export default JobRelatedFilePreview;

type AudioPreviewProps = {
  isdark: number;
};

const AudioPreview = styled.div<AudioPreviewProps>`
  audio {
    width: 100%;
    filter: ${({ isdark }) => (isdark ? 'invert(1)' : 'none')};
  }
`;

const CannotBePreviewed = styled.div`
  padding: 30px;
  display: flex;
  flex-direction: row;
  font-size: 1.3em;
  justify-content: center;
  align-items: center;
`;

const Metadata = styled.div`
  margin-bottom: ${props => props.theme.spacing(1)};
`;

const CapitalizedChip = styled(Chip)`
  text-transform: capitalize;

  && {
    border-radius: 5px;
    height: 26px;
    margin-top: 6px;
    margin-bottom: 4px;
    margin-right: 10px;
    max-width: 15em;
    font-size: 0.8rem !important;

    ${({ theme }) => theme.breakpoints.down('lg')} {
      width: 0px;
      height: 0px;
      visibility: hidden;
    }

    .MuiChip-label {
      font-size: 0.8rem !important;
      display: inline-block;
      max-width: 15em;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;